import React, { useState, useEffect } from 'react';
import {
  Trophy,
  Users,
  MapPin,
  Calendar,
  Clock,
  Shield,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  X,
  Compass,
  MessageCircle,
  Flame,
  ExternalLink,
  Sparkles,
  Info,
  Check,
  AlertCircle
} from 'lucide-react';

export interface GameGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'host' | 'join';
  onOpenCreate?: () => void;
  onExploreGames?: () => void;
}

type GuideMode = 'host' | 'join';

export default function GameGuideModal({
  isOpen,
  onClose,
  initialMode = 'host',
  onOpenCreate,
  onExploreGames,
}: GameGuideModalProps) {
  const [mode, setMode] = useState<GuideMode>(initialMode);
  const [step, setStep] = useState<number>(1);

  // Interactive mock states for preview widgets
  const [mockSport, setMockSport] = useState<string>('cricket');
  const [mockSkill, setMockSkill] = useState<string>('casual');
  const [mockDay, setMockDay] = useState<string>('today');
  const [mockWaiverChecked, setMockWaiverChecked] = useState<boolean>(true);
  const [mockFilter, setMockFilter] = useState<'all' | 'need1'>('need1');

  useEffect(() => {
    if (initialMode) {
      setMode(initialMode);
      setStep(1);
    }
  }, [initialMode, isOpen]);

  // Keyboard navigation: Escape closes, Arrow keys switch steps
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        setStep((prev) => Math.min(4, prev + 1));
      } else if (e.key === 'ArrowLeft') {
        setStep((prev) => Math.max(1, prev - 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const totalSteps = 4;

  const handleModeSwitch = (newMode: GuideMode) => {
    setMode(newMode);
    setStep(1);
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep((prev) => prev + 1);
    } else {
      if (mode === 'host' && onOpenCreate) {
        onClose();
        onOpenCreate();
      } else if (mode === 'join' && onExploreGames) {
        onClose();
        onExploreGames();
      } else {
        onClose();
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((prev) => prev - 1);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        animation: 'fadeIn 0.2s ease-out',
      }}
    >
      <div
        className="modal-content guide-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '720px',
          maxHeight: '92vh',
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid #e5e7eb',
        }}
      >
        {/* Top Header */}
        <div
          style={{
            padding: '20px 24px 16px',
            borderBottom: '1px solid #f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#ffffff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
              }}
            >
              <Compass size={20} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: '#111827', letterSpacing: '-0.02em' }}>
                  KhelDrop Playbook & Guide
                </h2>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    padding: '2px 8px',
                    borderRadius: '999px',
                    background: '#f3e8ff',
                    color: '#7c3aed',
                  }}
                >
                  Quickstart
                </span>
              </div>
              <p style={{ margin: '2px 0 0', fontSize: '12.5px', color: '#6b7280' }}>
                Interactive step-by-step walkthrough for hosts and players
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close guide"
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border: '1px solid #e5e7eb',
              background: '#f9fafb',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <X size={17} />
          </button>
        </div>

        {/* Top Live Click-by-Click Banner */}
        <div
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.12) 0%, rgba(79, 70, 229, 0.08) 100%)',
            borderBottom: '1px solid rgba(124, 58, 237, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Sparkles size={16} color="#7c3aed" />
            <span style={{ fontSize: '13px', fontWeight: 800, color: '#581c87' }}>
              Want to click & test real features on KhelDrop?
            </span>
          </div>

          <button
            type="button"
            onClick={() => {
              onClose();
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('kheldrop_start_tour'));
              }
            }}
            style={{
              background: '#7c3aed',
              color: '#ffffff',
              border: 'none',
              borderRadius: '999px',
              padding: '7px 16px',
              fontSize: '12.5px',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 8px rgba(124, 58, 237, 0.4)',
              whiteSpace: 'nowrap',
            }}
          >
            Launch Click-by-Click Live Tour →
          </button>
        </div>

        {/* Mode Selector Tabs (Host vs Join) */}
        <div style={{ padding: '16px 24px 0', background: '#fafafa', borderBottom: '1px solid #f3f4f6' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              background: '#e5e7eb',
              padding: '4px',
              borderRadius: '14px',
            }}
          >
            <button
              type="button"
              onClick={() => handleModeSwitch('host')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px 14px',
                borderRadius: '10px',
                border: 'none',
                background: mode === 'host' ? '#ffffff' : 'transparent',
                color: mode === 'host' ? '#111827' : '#6b7280',
                fontWeight: mode === 'host' ? 800 : 600,
                fontSize: '13.5px',
                boxShadow: mode === 'host' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <Trophy size={16} color={mode === 'host' ? '#7c3aed' : '#6b7280'} />
              <span>Host an Event / Match</span>
            </button>

            <button
              type="button"
              onClick={() => handleModeSwitch('join')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px 14px',
                borderRadius: '10px',
                border: 'none',
                background: mode === 'join' ? '#ffffff' : 'transparent',
                color: mode === 'join' ? '#111827' : '#6b7280',
                fontWeight: mode === 'join' ? 800 : 600,
                fontSize: '13.5px',
                boxShadow: mode === 'join' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <Users size={16} color={mode === 'join' ? '#10b981' : '#6b7280'} />
              <span>Join a Game / Fill a Slot</span>
            </button>
          </div>

          {/* Step Pills Bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '6px',
              padding: '14px 0 12px',
              overflowX: 'auto',
            }}
          >
            {mode === 'host' ? (
              <>
                <StepPill num={1} label="Sport & Skill" active={step === 1} completed={step > 1} onClick={() => setStep(1)} />
                <StepConnector done={step > 1} />
                <StepPill num={2} label="Venue & Time" active={step === 2} completed={step > 2} onClick={() => setStep(2)} />
                <StepConnector done={step > 2} />
                <StepPill num={3} label="Slots & Rules" active={step === 3} completed={step > 3} onClick={() => setStep(3)} />
                <StepConnector done={step > 3} />
                <StepPill num={4} label="Publish Lobby" active={step === 4} completed={step === 4} onClick={() => setStep(4)} />
              </>
            ) : (
              <>
                <StepPill num={1} label="Radar & Filters" active={step === 1} completed={step > 1} onClick={() => setStep(1)} />
                <StepConnector done={step > 1} />
                <StepPill num={2} label="Match Intel" active={step === 2} completed={step > 2} onClick={() => setStep(2)} />
                <StepConnector done={step > 2} />
                <StepPill num={3} label="Safety Waiver" active={step === 3} completed={step > 3} onClick={() => setStep(3)} />
                <StepConnector done={step > 3} />
                <StepPill num={4} label="Lock & Play" active={step === 4} completed={step === 4} onClick={() => setStep(4)} />
              </>
            )}
          </div>
        </div>

        {/* Scrollable Body Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px',
            background: '#ffffff',
          }}
        >
          {mode === 'host' ? (
            /* =========================================================================
               HOSTING WALKTHROUGH
               ========================================================================= */
            <div>
              {/* Host Step 1: Sport & Skill */}
              {step === 1 && (
                <div>
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#7c3aed', background: '#f3e8ff', padding: '2px 8px', borderRadius: '6px' }}>
                        STEP 1 OF 4
                      </span>
                      <h3 style={{ fontSize: '19px', fontWeight: 800, color: '#111827', margin: 0 }}>
                        Select Sport & Experience Level
                      </h3>
                    </div>
                    <p style={{ fontSize: '13.5px', color: '#4b5563', lineHeight: 1.5, margin: 0 }}>
                      Choose which sport you are organizing and set the competitiveness so players know what to expect.
                    </p>
                  </div>

                  {/* Interactive Preview Widget */}
                  <div
                    style={{
                      background: '#f9fafb',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '16px',
                      padding: '16px',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '10px', letterSpacing: '0.05em' }}>
                      Interactive Preview (Tap to test):
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px', marginBottom: '14px' }}>
                      {[
                        { id: 'cricket', name: 'Cricket', icon: <svg width='1em' height='1em' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='M18.8 4L20 5.2a1.4 1.4 0 010 2L10 17.2a1.4 1.4 0 01-2 0L6.8 16a1.4 1.4 0 010-2L16.8 4a1.4 1.4 0 012 0z'/><path d='M4 20h.01'/><path d='M8 20h.01'/><path d='M6 18h.01'/></svg>, count: '10-14 players' },
                        { id: 'football', name: 'Football', icon: <svg width='1em' height='1em' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><circle cx='12' cy='12' r='10'/><path d='M12 7l-3.5 6h7z'/><path d='M12 7l3.5 6 4-2'/><path d='M12 7l-3.5 6-4-2'/><path d='M4.5 11l4 2 1.5 5'/><path d='M19.5 11l-4 2-1.5 5'/></svg>, count: '10-12 players' },
                        { id: 'badminton', name: 'Badminton', icon: <svg width='1em' height='1em' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='M18.2 5.8a4.5 4.5 0 00-6.4 0L4 13.6c-2.7 2.7-2.7 7.1 0 9.8a7 7 0 009.8 0L21.6 15.6a4.5 4.5 0 000-6.4l-3.4-3.4z'/><path d='M13.6 13.6l3.4-3.4'/><path d='M10.2 17l3.4-3.4'/></svg>, count: '2-4 players' },
                        { id: 'basketball', name: 'Basketball', icon: <svg width='1em' height='1em' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><circle cx='12' cy='12' r='10'/><path d='M8.56 2.75c4.37 6.03 6.02 9.42 3.42 17.02'/><path d='M15.44 2.75c-4.37 6.03-6.02 9.42-3.42 17.02'/><path d='M2.75 8.56c6.03-4.37 9.42-6.02 17.02-3.42'/><path d='M2.75 15.44c6.03 4.37 9.42 6.02 17.02 3.42'/></svg>, count: '6-10 players' },
                      ].map((s) => {
                        const isSelected = mockSport === s.id;
                        return (
                          <div
                            key={s.id}
                            onClick={() => setMockSport(s.id)}
                            style={{
                              padding: '10px 12px',
                              borderRadius: '12px',
                              border: isSelected ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                              background: isSelected ? '#f5f3ff' : '#ffffff',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <span style={{ fontSize: '18px' }}>{s.icon}</span>
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: isSelected ? '#7c3aed' : '#111827' }}>
                                {s.name}
                              </div>
                              <div style={{ fontSize: '10.5px', color: '#6b7280' }}>{s.count}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[
                        { id: 'casual', label: 'Casual / Friendly', desc: 'Chill & fun for all levels' },
                        { id: 'intermediate', label: 'Intermediate', desc: 'Active players with basic skills' },
                        { id: 'competitive', label: 'Competitive', desc: 'High intensity match' },
                      ].map((lvl) => (
                        <button
                          key={lvl.id}
                          type="button"
                          onClick={() => setMockSkill(lvl.id)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            borderRadius: '10px',
                            border: mockSkill === lvl.id ? '1.5px solid #111827' : '1px solid #e5e7eb',
                            background: mockSkill === lvl.id ? '#111827' : '#ffffff',
                            color: mockSkill === lvl.id ? '#ffffff' : '#4b5563',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {lvl.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '12px 14px', borderRadius: '12px', display: 'flex', gap: '10px' }}>
                    <CheckCircle2 size={16} color="#16a34a" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ fontSize: '12.5px', color: '#166534', lineHeight: 1.45 }}>
                      <strong>Captain's Tip:</strong> Specifying an accurate skill level prevents blowout matches and ensures everyone gets balanced playtime.
                    </div>
                  </div>
                </div>
              )}

              {/* Host Step 2: Venue & 7-Day Window */}
              {step === 2 && (
                <div>
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#7c3aed', background: '#f3e8ff', padding: '2px 8px', borderRadius: '6px' }}>
                        STEP 2 OF 4
                      </span>
                      <h3 style={{ fontSize: '19px', fontWeight: 800, color: '#111827', margin: 0 }}>
                        Pick Ground / Turf & 7-Day Time Window
                      </h3>
                    </div>
                    <p style={{ fontSize: '13.5px', color: '#4b5563', lineHeight: 1.5, margin: 0 }}>
                      KhelDrop operates a rolling 7-day calendar window (Today through Next 6 Days) to keep matches active and eliminate ghost lobbies.
                    </p>
                  </div>

                  {/* Interactive Date & Venue Preview */}
                  <div
                    style={{
                      background: '#f9fafb',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '16px',
                      padding: '16px',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '10px', letterSpacing: '0.05em' }}>
                      Rolling 7-Day Window (Tap to test):
                    </div>

                    <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '6px', marginBottom: '14px' }}>
                      {[
                        { id: 'today', day: 'Today', date: 'Oct 5' },
                        { id: 'tomorrow', day: 'Tomorrow', date: 'Oct 6' },
                        { id: 'wed', day: 'Wed', date: 'Oct 7' },
                        { id: 'thu', day: 'Thu', date: 'Oct 8' },
                        { id: 'fri', day: 'Fri', date: 'Oct 9' },
                      ].map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => setMockDay(d.id)}
                          style={{
                            minWidth: '76px',
                            padding: '10px 8px',
                            borderRadius: '12px',
                            border: mockDay === d.id ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                            background: mockDay === d.id ? '#7c3aed' : '#ffffff',
                            color: mockDay === d.id ? '#ffffff' : '#111827',
                            cursor: 'pointer',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: '11px', fontWeight: 600, opacity: 0.9 }}>{d.day}</div>
                          <div style={{ fontSize: '13px', fontWeight: 800 }}>{d.date}</div>
                        </button>
                      ))}
                    </div>

                    {/* Venue Card Simulation */}
                    <div
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '12px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(124, 58, 237, 0.1)', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <MapPin size={17} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: '#111827' }}>
                            Yamuna Sports Complex (Turf #2)
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#6b7280' }}>
                            Surajmal Vihar, East Delhi • Coarse GPS Pin Attached
                          </div>
                        </div>
                      </div>
                      <span style={{ fontSize: '11px', background: '#dcfce7', color: '#15803d', padding: '3px 8px', borderRadius: '6px', fontWeight: 700 }}>
                        Verified Turf
                      </span>
                    </div>
                  </div>

                  <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', padding: '12px 14px', borderRadius: '12px', display: 'flex', gap: '10px' }}>
                    <Info size={16} color="#7c3aed" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ fontSize: '12.5px', color: '#5b21b6', lineHeight: 1.45 }}>
                      <strong>Turnaround Time:</strong> Matches created 2 to 6 hours before kickoff fill slots 3x faster than last-minute postings!
                    </div>
                  </div>
                </div>
              )}

              {/* Host Step 3: Slots & Legal Covenants */}
              {step === 3 && (
                <div>
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#7c3aed', background: '#f3e8ff', padding: '2px 8px', borderRadius: '6px' }}>
                        STEP 3 OF 4
                      </span>
                      <h3 style={{ fontSize: '19px', fontWeight: 800, color: '#111827', margin: 0 }}>
                        Player Capacity & Statutory Rules
                      </h3>
                    </div>
                    <p style={{ fontSize: '13.5px', color: '#4b5563', lineHeight: 1.5, margin: 0 }}>
                      Set player capacity, understand the auto-waitlist, and affirm statutory zero-gambling compliance.
                    </p>
                  </div>

                  {/* Slot Breakdown Preview */}
                  <div
                    style={{
                      background: '#f9fafb',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '16px',
                      padding: '16px',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: 800, fontSize: '13px', color: '#111827' }}>Squad Roster Breakdown</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#ef4444' }}><svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' style={{display: 'inline', verticalAlign: 'middle', marginRight: 4}}><path d='M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z'/></svg> 1 Slot Remaining</span>
                    </div>

                    <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                      {/* Host Slot */}
                      <div style={{ flex: 1, padding: '8px 4px', background: '#111827', color: '#ffffff', borderRadius: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 700 }}>
                        #1 HOST (You)
                      </div>
                      {/* Filled Slots */}
                      {[2, 3, 4, 5, 6, 7, 8, 9].map((slot) => (
                        <div key={slot} style={{ flex: 1, padding: '8px 4px', background: '#dcfce7', color: '#166534', borderRadius: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 700 }}>
                          #{slot}
                        </div>
                      ))}
                      {/* Empty Slot */}
                      <div style={{ flex: 1, padding: '8px 4px', background: '#fee2e2', border: '1.5px dashed #ef4444', color: '#b91c1c', borderRadius: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 800 }}>
                        #10 NEED 1
                      </div>
                    </div>

                    <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.45 }}>
                      • <strong>Slot #1 is yours:</strong> As host, you automatically fill slot #1 upon creation.<br />
                      • <strong>Automated Waitlist:</strong> If all 10 slots fill, extra players join the standby waitlist and auto-promote if someone ditches.
                    </div>
                  </div>

                  {/* Statutory Zero-Betting Card */}
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '12px 14px', borderRadius: '12px', display: 'flex', gap: '10px' }}>
                    <Shield size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ fontSize: '12px', color: '#991b1b', lineHeight: 1.45 }}>
                      <strong>Statutory Legal Compliance (Indian Public Gambling Act & BNS 2023):</strong> KhelDrop is strictly a recreational sports community. Any form of betting, cash wagers, or gambling on match outcomes is strictly prohibited and results in immediate permanent ban and law enforcement escalation.
                    </div>
                  </div>
                </div>
              )}

              {/* Host Step 4: Publish & Manage Lobby */}
              {step === 4 && (
                <div>
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', background: '#d1fae5', padding: '2px 8px', borderRadius: '6px' }}>
                        STEP 4 OF 4
                      </span>
                      <h3 style={{ fontSize: '19px', fontWeight: 800, color: '#111827', margin: 0 }}>
                        Publish, Match Radar & Arrival Check-in
                      </h3>
                    </div>
                    <p style={{ fontSize: '13.5px', color: '#4b5563', lineHeight: 1.5, margin: 0 }}>
                      Once published, your game appears on the live radar for players within your chosen radius.
                    </p>
                  </div>

                  {/* Live Lobby Mockup */}
                  <div
                    style={{
                      background: '#f9fafb',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '16px',
                      padding: '16px',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '14px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 800, background: '#111827', color: '#ffffff', padding: '2px 8px', borderRadius: '6px' }}>
                          LIVE LOBBY
                        </span>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: '#7c3aed' }}>
                          Starts Today at 6:30 PM
                        </span>
                      </div>
                      <div style={{ fontWeight: 800, fontSize: '15px', color: '#111827', marginBottom: '4px' }}>
                        Weekend Box Cricket Derby (5v5)
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        Yamuna Sports Complex • 9 of 10 Players Confirmed
                      </div>

                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button
                          type="button"
                          style={{
                            flex: 1,
                            padding: '8px',
                            borderRadius: '8px',
                            background: '#f3f4f6',
                            border: '1px solid #e5e7eb',
                            color: '#111827',
                            fontSize: '12px',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '5px',
                          }}
                        >
                          <MessageCircle size={14} /> Open Squad Chat
                        </button>
                        <button
                          type="button"
                          style={{
                            flex: 1,
                            padding: '8px',
                            borderRadius: '8px',
                            background: '#10b981',
                            border: 'none',
                            color: '#ffffff',
                            fontSize: '12px',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '5px',
                          }}
                        >
                          <Check size={14} /> Turf Check-In
                        </button>
                      </div>
                    </div>

                    <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.45 }}>
                      • <strong>Private Squad Chat:</strong> Coordinate who is bringing balls/bats and verify jerseys.<br />
                      • <strong>GPS Geofence Arrival:</strong> Upon reaching the turf, players tap Check-In to mark attendance.
                    </div>
                  </div>

                  <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '12px 14px', borderRadius: '12px', display: 'flex', gap: '10px' }}>
                    <Sparkles size={16} color="#059669" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ fontSize: '12.5px', color: '#065f46', lineHeight: 1.45 }}>
                      <strong>Ready to host your match?</strong> You have complete control as match captain. Tap below to launch your first game!
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* =========================================================================
               JOINING WALKTHROUGH
               ========================================================================= */
            <div>
              {/* Join Step 1: Radar & Sport Filters */}
              {step === 1 && (
                <div>
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', background: '#d1fae5', padding: '2px 8px', borderRadius: '6px' }}>
                        STEP 1 OF 4
                      </span>
                      <h3 style={{ fontSize: '19px', fontWeight: 800, color: '#111827', margin: 0 }}>
                        Explore Match Radar & Sport Filters
                      </h3>
                    </div>
                    <p style={{ fontSize: '13.5px', color: '#4b5563', lineHeight: 1.5, margin: 0 }}>
                      Discover recreational matches happening around your neighborhood sorted by distance and kickoff time.
                    </p>
                  </div>

                  {/* Interactive Filter Preview */}
                  <div
                    style={{
                      background: '#f9fafb',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '16px',
                      padding: '16px',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '10px', letterSpacing: '0.05em' }}>
                      Interactive Filter Bar (Tap to toggle):
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => setMockFilter('all')}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          border: mockFilter === 'all' ? '2px solid #111827' : '1px solid #e5e7eb',
                          background: mockFilter === 'all' ? '#111827' : '#ffffff',
                          color: mockFilter === 'all' ? '#ffffff' : '#111827',
                          fontWeight: 700,
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        All Sports (14)
                      </button>

                      <button
                        type="button"
                        onClick={() => setMockFilter('need1')}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          border: mockFilter === 'need1' ? '2px solid #ef4444' : '1px solid #fecaca',
                          background: mockFilter === 'need1' ? '#fef2f2' : '#ffffff',
                          color: '#dc2626',
                          fontWeight: 700,
                          fontSize: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                        }}
                      >
                        <Flame size={14} color="#dc2626" /> Only Games Needing 1 Player (4)
                      </button>
                    </div>

                    <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.45 }}>
                      • <strong>The "Need 1 Player" Filter:</strong> When you want to play immediately, toggle this filter to find teams waiting for that final person to start the match!
                    </div>
                  </div>

                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '12px 14px', borderRadius: '12px', display: 'flex', gap: '10px' }}>
                    <Compass size={16} color="#16a34a" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ fontSize: '12.5px', color: '#166534', lineHeight: 1.45 }}>
                      <strong>Privacy Protected:</strong> Your device GPS calculates distances on-device. Other players never see your personal home coordinates.
                    </div>
                  </div>
                </div>
              )}

              {/* Join Step 2: Match Intel & Directions */}
              {step === 2 && (
                <div>
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', background: '#d1fae5', padding: '2px 8px', borderRadius: '6px' }}>
                        STEP 2 OF 4
                      </span>
                      <h3 style={{ fontSize: '19px', fontWeight: 800, color: '#111827', margin: 0 }}>
                        Review Match Intel & Turf Directions
                      </h3>
                    </div>
                    <p style={{ fontSize: '13.5px', color: '#4b5563', lineHeight: 1.5, margin: 0 }}>
                      Inspect match details, host credibility, start time, and open Google Maps directions to the turf.
                    </p>
                  </div>

                  {/* Match Card Simulation */}
                  <div
                    style={{
                      background: '#f9fafb',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '16px',
                      padding: '16px',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 800, background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Flame size={12} /> ONE MORE PLAYER NEEDED
                        </span>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: '#16a34a' }}>
                          Starts in 45m
                        </span>
                      </div>

                      <div style={{ fontWeight: 800, fontSize: '15px', color: '#111827', marginBottom: '4px' }}>
                        Evening 5v5 Football Turf Clash
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <MapPin size={13} /> 1.4 km away
                        </span>
                        <span>•</span>
                        <span>Host: Ashutosh (Verified)</span>
                        <span>•</span>
                        <span>Casual / Intermediate</span>
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <a
                          href="https://maps.google.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.preventDefault()}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            background: '#f3f4f6',
                            border: '1px solid #e5e7eb',
                            color: '#111827',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            textDecoration: 'none',
                          }}
                        >
                          Google Maps Directions <ExternalLink size={12} />
                        </a>
                      </div>
                    </div>
                  </div>

                  <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', padding: '12px 14px', borderRadius: '12px', display: 'flex', gap: '10px' }}>
                    <Info size={16} color="#7c3aed" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ fontSize: '12.5px', color: '#5b21b6', lineHeight: 1.45 }}>
                      <strong>Zero Hidden Fees:</strong> KhelDrop has no paywalls or commission deductions for joining community games.
                    </div>
                  </div>
                </div>
              )}

              {/* Join Step 3: Safety Waiver */}
              {step === 3 && (
                <div>
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', background: '#d1fae5', padding: '2px 8px', borderRadius: '6px' }}>
                        STEP 3 OF 4
                      </span>
                      <h3 style={{ fontSize: '19px', fontWeight: 800, color: '#111827', margin: 0 }}>
                        Sign the Statutory Safety Waiver
                      </h3>
                    </div>
                    <p style={{ fontSize: '13.5px', color: '#4b5563', lineHeight: 1.5, margin: 0 }}>
                      Under Indian law, participating in contact sports carries inherent physical risks. KhelDrop requires a 1-tap statutory waiver to protect both players and hosts.
                    </p>
                  </div>

                  {/* Interactive Waiver Card */}
                  <div
                    style={{
                      background: '#f9fafb',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '16px',
                      padding: '16px',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '10px', letterSpacing: '0.05em' }}>
                      Pre-Match Digital Consent Ledger:
                    </div>

                    <div
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px',
                        marginBottom: '12px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <Shield size={16} color="#7c3aed" />
                        <span style={{ fontWeight: 800, fontSize: '13px', color: '#111827' }}>
                          Volenti Non Fit Injuria & Physical Risk Waiver
                        </span>
                      </div>
                      <p style={{ fontSize: '12px', color: '#4b5563', lineHeight: 1.45, margin: '0 0 10px' }}>
                        I confirm that I am 18+ years of age, physically fit for vigorous athletic exertion, participate voluntarily, and agree that KhelDrop and the match host act purely as communications facilitators.
                      </p>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="mock-guide-waiver"
                          checked={mockWaiverChecked}
                          onChange={(e) => setMockWaiverChecked(e.target.checked)}
                          style={{ accentColor: '#7c3aed', cursor: 'pointer' }}
                        />
                        <label htmlFor="mock-guide-waiver" style={{ fontSize: '12px', fontWeight: 700, color: '#111827', cursor: 'pointer' }}>
                          I solemnly affirm and accept the Match Safety Waiver
                        </label>
                      </div>
                    </div>

                    <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.45 }}>
                      • <strong>One-Time Record:</strong> Once affirmed for a match, your consent is logged in compliance with Indian IT Rules & DPDP Act 2023.
                    </div>
                  </div>

                  <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '12px 14px', borderRadius: '12px', display: 'flex', gap: '10px' }}>
                    <CheckCircle2 size={16} color="#059669" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ fontSize: '12.5px', color: '#065f46', lineHeight: 1.45 }}>
                      <strong>Strict 18+ Platform:</strong> KhelDrop is strictly adult-only. Minors are barred from entering matches to protect children and ensure lawful contracts.
                    </div>
                  </div>
                </div>
              )}

              {/* Join Step 4: Lock & Play */}
              {step === 4 && (
                <div>
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', background: '#d1fae5', padding: '2px 8px', borderRadius: '6px' }}>
                        STEP 4 OF 4
                      </span>
                      <h3 style={{ fontSize: '19px', fontWeight: 800, color: '#111827', margin: 0 }}>
                        Lock Slot, Join Squad Chat & Play!
                      </h3>
                    </div>
                    <p style={{ fontSize: '13.5px', color: '#4b5563', lineHeight: 1.5, margin: 0 }}>
                      Your slot is guaranteed. Coordinate with teammates in chat, arrive on time, and enjoy the game!
                    </p>
                  </div>

                  {/* Confirmed Slot Preview */}
                  <div
                    style={{
                      background: '#f9fafb',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '16px',
                      padding: '16px',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ background: '#ffffff', borderRadius: '12px', border: '1.5px solid #10b981', padding: '14px', textAlign: 'center' }}>
                      <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: '#dcfce7', color: '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                        <Check size={22} strokeWidth={3} />
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: 800, color: '#111827' }}>
                        Slot Reserved & Confirmed!
                      </div>
                      <div style={{ fontSize: '12.5px', color: '#6b7280', margin: '4px 0 12px' }}>
                        You are player #10 of 10. The squad is now complete!
                      </div>

                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f3f4f6', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, color: '#111827' }}>
                        <Clock size={13} /> Kickoff in 45 minutes
                      </div>
                    </div>

                    <div style={{ marginTop: '14px', fontSize: '12px', color: '#6b7280', lineHeight: 1.45 }}>
                      • <strong>Ditch Prevention:</strong> If an emergency arises, leave the match early so a waitlisted player can fill the slot.<br />
                      • <strong>Check-In at Venue:</strong> Open KhelDrop at the ground to confirm your presence on the radar.
                    </div>
                  </div>

                  <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '12px 14px', borderRadius: '12px', display: 'flex', gap: '10px' }}>
                    <Sparkles size={16} color="#059669" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ fontSize: '12.5px', color: '#065f46', lineHeight: 1.45 }}>
                      <strong>You're ready to play!</strong> Explore the live radar right now to jump into an active game.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer Controls */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#fafafa',
          }}
        >
          <button
            type="button"
            onClick={handleBack}
            disabled={step === 1}
            style={{
              padding: '10px 16px',
              borderRadius: '10px',
              border: '1px solid #e5e7eb',
              background: '#ffffff',
              color: step === 1 ? '#d1d5db' : '#374151',
              fontWeight: 700,
              fontSize: '13px',
              cursor: step === 1 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <ArrowLeft size={15} /> Back
          </button>

          {/* Dots Indicator */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                onClick={() => setStep(s)}
                style={{
                  width: step === s ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  background: step === s ? (mode === 'host' ? '#7c3aed' : '#10b981') : '#d1d5db',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              />
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={() => {
                onClose();
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('kheldrop_start_tour'));
                }
              }}
              style={{
                padding: '10px 16px',
                borderRadius: '10px',
                border: '1.5px solid #7c3aed',
                background: 'rgba(124, 58, 237, 0.08)',
                color: '#7c3aed',
                fontWeight: 800,
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Sparkles size={14} /> Click-by-Click Live Tour
            </button>

            <button
              type="button"
              onClick={handleNext}
              style={{
                padding: '10px 20px',
                borderRadius: '10px',
                border: 'none',
                background: mode === 'host' ? '#7c3aed' : '#10b981',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '13.5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: mode === 'host' ? '0 4px 12px rgba(124, 58, 237, 0.25)' : '0 4px 12px rgba(16, 185, 129, 0.25)',
                transition: 'all 0.15s ease',
              }}
            >
            {step < totalSteps ? (
              <>
                Next Step <ArrowRight size={15} />
              </>
            ) : mode === 'host' ? (
              <>
                Start Hosting Now <Trophy size={15} />
              </>
            ) : (
              <>
                Find Matches Near Me <Compass size={15} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  </div>
  );
}

/* Helper Components */
function StepPill({
  num,
  label,
  active,
  completed,
  onClick,
}: {
  num: number;
  label: string;
  active: boolean;
  completed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        background: active ? '#111827' : completed ? '#f3f4f6' : 'transparent',
        border: active ? '1.5px solid #111827' : '1px solid transparent',
        color: active ? '#ffffff' : completed ? '#374151' : '#9ca3af',
        padding: '5px 10px',
        borderRadius: '8px',
        fontSize: '11.5px',
        fontWeight: active || completed ? 700 : 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'all 0.15s ease',
      }}
    >
      <span
        style={{
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: active ? '#ffffff' : completed ? '#10b981' : '#e5e7eb',
          color: active ? '#111827' : completed ? '#ffffff' : '#6b7280',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '10px',
          fontWeight: 800,
        }}
      >
        {completed ? <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' style={{display: 'inline', verticalAlign: 'middle'}}><polyline points='20 6 9 17 4 12'/></svg> : num}
      </span>
      <span>{label}</span>
    </button>
  );
}

function StepConnector({ done }: { done: boolean }) {
  return (
    <div
      style={{
        width: '16px',
        height: '2px',
        background: done ? '#10b981' : '#e5e7eb',
        flexShrink: 0,
      }}
    />
  );
}
